import { createSelector } from 'reselect';
import type { UserState } from '@user/redux/reducers';
import { formatDistanceToNow } from 'date-fns';
import { PRIMARY_MARKETS } from '@components/StockScreener/constants/markets';
import { PlanType } from '@/components/OurPlans/components/Plans/constants';

export const getUser = (state): UserState => state.user;

const getAccessState = (state) => {
  const user = getUser(state);
  return user.access;
};

const getBillingState = (state) => {
  const user = getUser(state);
  return user.billing;
};
const getPlansState = (state) => {
  const user = getUser(state);
  return user.plans;
};

export const isUserTrialing = ({ user }: { user: UserState }): boolean =>
  user.billing.plan.status === 'trialing';

export const getUserBillingPlanId = ({ user }: { user: UserState }): string =>
  user.billing.plan.plan;

export const getIsUserCanceled = createSelector(
  [getBillingState],
  (billing) => billing.plan.canceled
);

const getFlags = (state) => state.flags;

export const getIsUserInitialised = createSelector(
  [getUser],
  (user) => user.initialised
);

export const getDaysUntilTrialEnds = createSelector(
  [getUser],
  (user): string => {
    const trialEnd = user.billing.plan.trialEnd;
    return trialEnd ? formatDistanceToNow(trialEnd) : '';
  }
);

export const getMatchedFeatureFlag = (showOn) =>
  createSelector([getFlags], (flags) => {
    return flags.showOn ? showOn : '';
  });

export const getFeatureFlagProp = () =>
  createSelector([getFlags], (flags) => flags.retrieveProps);

export const getUserCurrency = createSelector(
  [getUser],
  (user) => user.profile.currencyISO
);

export const getUserCountryISO = createSelector(
  [getUser],
  (user) => user.profile.countryISO
);

export const getUserDefaultMarket = createSelector(
  [getUser],
  (user) => user.profile.defaultMarketISO
);

export const getDefaultPortfolioId = createSelector([getUser], (user) => {
  return user.profile.defaultPortfolioId;
});

export const getSupportedUserCountry = createSelector(
  [getUserDefaultMarket],
  (defaultMarket) => {
    try {
      const isSupported =
        defaultMarket.toString().toLowerCase() in PRIMARY_MARKETS;
      return isSupported ? defaultMarket.toLowerCase() : '';
    } catch (e) {
      /** Default to US */
      return '';
    }
  }
);

export const getCanCreatePortfolios = (createdPortfolios: number) =>
  createSelector([getUser], (user) => {
    const {
      access: { createPortfolios },
    } = user;
    return createdPortfolios < createPortfolios || createPortfolios === -1; // -1 means can create infinite portfolios
  });

export const getDefaultCreditCard = createSelector([getUser], (user) => {
  const { billing } = user;
  if (billing.cards.length) {
    return billing.cards[0];
  }
  return null;
});

export const getUserEmail = createSelector(
  [getUser],
  (user) => user.profile.emailAddress || ''
);

export const getUserSelectedMarket = createSelector(
  [getUser],
  (user): string => user.selectedMarket
);

export const getNextUpsellPlan = createSelector([getPlansState], (plans) => {
  const plan = Object.values(plans).filter(
    (plan) => plan.type === PlanType.Investor
  );
  return plan[0];
});

export const getIsUserLoggedIn = createSelector(
  [getUser],
  (user) => user.profile.loggedIn
);

export const getViewableCompanyReports = createSelector(
  [getAccessState],
  (access) => {
    return access.viewCompanyReports;
  }
);

export const hasLimitedCompanyViews = createSelector(
  [getViewableCompanyReports],
  (viewableReports) => {
    return viewableReports !== -1;
  }
);

export const getAdvisorProratedPrice = createSelector(
  [getBillingState],
  (billing) => billing.plan.proratedAdvisorPlanPrice
);

export const getCanGetCompanyReportPdf = createSelector(
  [getAccessState],
  (access) => access.canGetCompanyReportPdf
);

export const getCanGetCompanyReportCsv = createSelector(
  [getAccessState],
  (access) => access.canGetCompanyReportCsv
);

export const getCanGetPortfolioReportCsv = createSelector(
  [getAccessState],
  (access) => access.canGetPortfolioReportCsv
);

export const getCanDistributeCompanyReportPdf = createSelector(
  [getAccessState],
  (access) => access.canDistributeCompanyReportPdf
);

export const getPlanType = createSelector(
  [getAccessState],
  (access) => access.planType
);

export const getPlanName = (planId?: string) =>
  createSelector([getPlansState, getAccessState], (plans, userAccess) => {
    const planTargetId = planId ? planId : userAccess.plan;
    const plan = Object.values(plans).filter(
      (plan) => plan.id === planTargetId
    );
    return plan[0] ? plan[0].name : '';
  });

/** polyfill for all the MS Edge users */
function fromEntries(iterable) {
  if ('fromEntries' in Object) {
    return Object.fromEntries(iterable);
  }
  return [...iterable].reduce((obj, [key, val]) => {
    obj[key] = val;
    return obj;
  }, {});
}

export const getPlanAvailableFeatures = createSelector(
  [getPlansState],
  (plans) => {
    const planList = Object.values(plans);
    let availableFeatures;
    planList.forEach((plan) => {
      const planFeatures = {
        [plan.id]: {
          ...fromEntries(
            Object.entries(plan.features).filter((item) => item[1])
          ),
        },
      };
      availableFeatures = { ...availableFeatures, ...planFeatures };
    });
    return availableFeatures;
  }
);

export const getPlanId = (planType: string) =>
  createSelector([getPlansState], (plans) => {
    const plan = Object.values(plans).filter((plan) => plan.type === planType);
    return plan[0] ? plan[0].id : '';
  });

/** Get validated userPlanId */
export const getUserAccessPlanId = createSelector(
  [getAccessState, getPlansState],
  (userAccess, plans) => {
    const userPlan = Object.values(plans).filter(
      (plan) => plan.id === userAccess.plan || plan.type === userAccess.planType
    );
    return (userPlan[0] && userPlan[0].id) || userAccess.plan;
  }
);

export /** Get one plan by type */
const getPlan = (planType: PlanType) =>
  createSelector([getPlansState], (plans) => {
    const plan = Object.values(plans).filter((plan) => plan.type === planType);
    return plan[0];
  });

//TODO: refactor or remove once advisor page is reviewed

// export const getAdvisorPlanFinalPrice = createSelector(
//   [
//     getAdvisorProratedPrice,
//     getPlan(PlanType.Advisor),
//     isUserOnFreePlan,
//     isUserTrialing,
//     getCoupon,
//   ],
//   (advisorProratedPrice, advisor, isFreePlan, isTrialing, coupon) => {
//     if (!advisor) return 0;
//     else {
//       const discountedPrice =
//         coupon.percentOff > 0
//           ? advisor.amount * (1 - coupon.percentOff)
//           : advisor.amount;
//       const finalPrice =
//         advisorProratedPrice > 0 && !isFreePlan && !isTrialing
//           ? advisorProratedPrice
//           : discountedPrice;
//       return finalPrice;
//     }
//   }
// );

/** Returns plan price */
export const getPlanDisplayingPrice = (planType: PlanType) =>
  createSelector([getPlan(planType)], (plan) => {
    if (!plan) return 0;
    return plan.amount;
  });

//TODO: refactor or remove once advisor page is reviewed

// export const getPlanCTA = (plan: Plan) =>
//   createSelector(
//     [
//       getIsUserLoggedIn,
//       isUserTrialing,
//       isUserOnFreePlan,
//       getIsUserCanceled,
//       getUserAccessPlanId,
//       getPlanType,
//     ],
//     (
//       isLoggedIn,
//       isTrialing,
//       isUserOnFreePlan,
//       isUserCancel,
//       userPlanId,
//       userPlanType
//     ) => {
//       if (!plan || !isLoggedIn) return PlanCTA.Subscribe;
//       if (isTrialing || isUserOnFreePlan) return PlanCTA.Upgrade;
//       if (userPlanId === plan.id && !isUserCancel) return PlanCTA.Current;
//       if (userPlanId === plan.id && isUserCancel) return PlanCTA.Upgrade;
//       if (userPlanId !== plan.id && isUserCancel) return PlanCTA.Disabled;
//       if (plan && userPlanType !== '')
//         return planButtonCTA[userPlanType][plan.type];
//       else return PlanCTA.Upgrade;
//     }
//   );
